import {
    type ReactNode,
    useState,
    useEffect,
    useLayoutEffect,
    useRef,
    type CSSProperties,
    type SyntheticEvent,
} from 'react';
import noop from 'lodash/noop';
import * as Popover from '@radix-ui/react-popover';
import { AnchoredPopoverArrow, AnchoredPopoverWrapper } from './view';

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

/**
 * Позиционируемый поповер, «привязанный» к DOM-элементу по CSS-селектору.
 *
 * Компонент вычисляет геометрию целевого элемента через `getBoundingClientRect`,
 * выбирает сторону появления (учитывая края вьюпорта) и отрисовывает Radix Popover,
 * используя невидимый `<Popover.Anchor>` фиксированного размера как якорь.
 *
 * Источники изменений геометрии:
 * - скролл (в т.ч. внутри контейнеров) — глобальный listener с capture;
 * - ресайз окна;
 * - мутации DOM (`MutationObserver`) — на случай перестроений вне целевого узла;
 * - ресайз самого целевого узла (`ResizeObserver`).
 *
 * Управление открытием — контролируемое: `open` и `onOpenChange`.
 *
 * Ограничения:
 * - `selector` должен стабильно находить один элемент. При отсутствии элемента рендера нет.
 * - Компонент должен монтироваться в браузере (использует `window`, `document`).
 */
export type AnchoredPopoverProps = {
    /** CSS-селектор для поиска целевого DOM-элемента. */
    selector: string;
    /** Признак открытия поповера (контролируемое состояние). */
    open: boolean;
    /** Колбэк изменения состояния открытия. По умолчанию `noop`. */
    onOpenChange?: (next: boolean) => void;
    /** Содержимое поповера. */
    children: ReactNode;
    /**
     * Выравнивание точки привязки относительно цели.
     * @default 'center'
     */
    align?: Align;
    /**
     * Максимальная ширина контента поповера.
     * @default undefined
     */
    maxWidth?: number | string;
    /**
     * Предпочтительная сторона относительно целевого элемента.
     * Может быть переопределена логикой коллизий.
     * @default 'bottom'
     */
    side?: Side;
    /**
     * Отступ между якорем и контентом поповера.
     * @default 3
     */
    sideOffset?: number;
    /**
     * Ширина стрелки поповера в px.
     * Используется в вычислениях зоны коллизий и размеров якоря.
     * @default 30
     */
    arrowWidth?: number;
    /**
     * Высота стрелки поповера в px.
     * Используется в вычислениях зоны коллизий и размеров якоря.
     * @default 11
     */
    arrowHeight?: number;
    /**
     * Показывать стрелку поповера.
     * @default true
     */
    withArrow?: boolean;
    /**
     * Отступ центра стрелки от края контента поповера.
     * Позволяет соблюдать минимальные отступы от краёв вьюпорта.
     * @default 32
     */
    arrowEdgeOffset?: number;
    /**
     * Использовать поиск максимального z-index
     * @default true
     */
    withSearchTopLevelZIndex?: boolean;
    // Функция для перехода на следующий шаг
    next: (e?: SyntheticEvent) => void;
    /**
     * Не переносить фокус в поповер при открытии.
     * По умолчанию Radix фокусирует содержимое.
     * @default false
     */
    preventAutoFocus?: boolean;
};

// Минимальная дистанция от края вьюпорта
const MIN_PAD = 23;

const preventPopoverAutoFocus = (event: Event) => {
    event.preventDefault();
};

/** Возвращает z-index + 1 верхнего (ближайшего к body) positioned-предка с явным z-index. */
const getTopLevelZIndex = (element: HTMLElement): number | undefined => {
    let current: HTMLElement | null = element;
    let topZ: number | undefined;

    while (current && current !== document.body) {
        const s = window.getComputedStyle(current);

        if (s.position !== 'static' && s.zIndex !== 'auto') {
            const z = Number.parseInt(s.zIndex, 10);
            if (!Number.isNaN(z)) {
                topZ = z;
            }
        }

        current = current.parentElement;
    }

    return topZ === undefined ? undefined : topZ + 1;
};

export function AnchoredPopover({
    selector,
    open,
    onOpenChange = noop,
    children,
    align = 'center',
    maxWidth,
    side = 'bottom',
    sideOffset = 3,
    arrowWidth = 30,
    arrowHeight = 11,
    withArrow = true,
    arrowEdgeOffset = 32,
    withSearchTopLevelZIndex = true,
    next,
    preventAutoFocus = false,
}: AnchoredPopoverProps) {
    const targetRef = useRef<HTMLElement | null>(null);
    const [rect, setRect] = useState<DOMRect | null>(null);
    const [layerZIndex, setLayerZIndex] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        if (!withSearchTopLevelZIndex) return;

        const el = document.querySelector<HTMLElement>(selector);
        const nextZ = el ? getTopLevelZIndex(el) : undefined;
        setLayerZIndex(prev => (prev === nextZ ? prev : nextZ));
    }, [selector, withSearchTopLevelZIndex, open]);

    useEffect(() => {
        // Поиск элемента по селектору и первичное измерение + подписки
        targetRef.current = document.querySelector<HTMLElement>(selector) ?? null;

        const el = targetRef.current;
        if (!el) {
            if (open) {
                next();
            } else {
                setRect(null);
            }

            return;
        }

        const measure = () => {
            const r = el.getBoundingClientRect();
            setRect(prev =>
                !prev ||
                prev.left !== r.left ||
                prev.top !== r.top ||
                prev.width !== r.width ||
                prev.height !== r.height
                    ? r
                    : prev,
            );
        };

        // Первое измерение
        measure();

        // Реакция на скролл и ресайз
        const onScroll = () => measure();
        const onResize = () => measure();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);

        // Мутации документа и ресайз цели
        const mo = new MutationObserver(measure);
        mo.observe(document.documentElement, {
            childList: true,
            subtree: true,
        });

        const ro = 'ResizeObserver' in window ? new ResizeObserver(measure) : null;
        ro?.observe(el);

        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
            mo.disconnect();
            ro?.disconnect();
        };
    }, [next, open, selector]);

    // Если цели нет или нет измерений — ничего не рендерим.
    if (!targetRef.current || !rect) return null;

    const actualArrowWidth = withArrow ? arrowWidth : 0;
    const actualArrowHeight = withArrow ? arrowHeight : 0;
    const actualArrowEdgeOffset = withArrow ? arrowEdgeOffset : 0;
    const arrowCenterFromEdgeX = actualArrowEdgeOffset + actualArrowWidth / 2;
    const arrowCenterFromEdgeY = actualArrowEdgeOffset + actualArrowHeight / 2;

    const getAnchorStart = (start: number, size: number, arrowCenterFromEdge: number, value: Align) => {
        if (value === 'start') return start;
        if (value === 'end') return start + size - 2 * arrowCenterFromEdge;

        return start + size / 2 - arrowCenterFromEdge;
    };

    const anchorStartX = getAnchorStart(rect.left, rect.width, arrowCenterFromEdgeX, align);
    const anchorStartY = getAnchorStart(rect.top, rect.height, arrowCenterFromEdgeY, align);

    // anchor point along cross axis
    const cx = anchorStartX + arrowCenterFromEdgeX;
    const cy = anchorStartY + arrowCenterFromEdgeY;

    // Геометрия для расчёта безопасной позиции стрелки и стороны
    const guardX = Math.max(arrowCenterFromEdgeX + sideOffset, MIN_PAD + actualArrowWidth / 2 + sideOffset / 2);
    const guardY = Math.max(arrowCenterFromEdgeY + sideOffset, MIN_PAD + actualArrowHeight / 2 + sideOffset / 2);
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Выбор стороны до рендера (простая коллизионная логика)
    let computedSide: Side = side;

    if (side === 'top' || side === 'bottom') {
        const distLeft = cx;
        const distRight = vw - cx;
        if (distRight < guardX && distLeft >= distRight) {
            computedSide = 'left';
        } else if (distLeft < guardX && distLeft < distRight) {
            computedSide = 'right';
        }
    } else {
        const distTop = cy;
        const distBottom = vh - cy;
        if (distBottom < guardY && distTop >= distBottom) {
            computedSide = 'top';
        } else if (distTop < guardY && distTop < distBottom) {
            computedSide = 'bottom';
        }
    }

    // Невидимый якорь для Radix Popover.Anchor: фиксированный бокс возле цели
    let anchorStyle: CSSProperties;
    if (computedSide === 'left' || computedSide === 'right') {
        anchorStyle = {
            position: 'fixed',
            left: rect.left,
            top: anchorStartY,
            width: Math.max(1, rect.width),
            height: Math.max(1, 2 * arrowCenterFromEdgeY),
            pointerEvents: 'none',
            background: 'transparent',
        };
    } else {
        anchorStyle = {
            position: 'fixed',
            left: anchorStartX,
            top: rect.top,
            width: Math.max(1, 2 * arrowCenterFromEdgeX),
            height: Math.max(1, rect.height),
            pointerEvents: 'none',
            background: 'transparent',
        };
    }

    return (
        <Popover.Root
            open={open}
            onOpenChange={onOpenChange}
        >
            <Popover.Portal>
                <Popover.Anchor asChild>
                    <span
                        aria-hidden
                        style={anchorStyle}
                    />
                </Popover.Anchor>
            </Popover.Portal>

            <Popover.Portal>
                <AnchoredPopoverWrapper
                    side={computedSide}
                    align="start"
                    sideOffset={sideOffset}
                    collisionPadding={MIN_PAD}
                    sticky="partial"
                    $maxWidth={maxWidth}
                    $zIndex={layerZIndex}
                    onOpenAutoFocus={preventAutoFocus ? preventPopoverAutoFocus : undefined}
                >
                    {children}
                    {withArrow && (
                        <AnchoredPopoverArrow
                            width={arrowWidth}
                            height={arrowHeight}
                        />
                    )}
                </AnchoredPopoverWrapper>
            </Popover.Portal>
        </Popover.Root>
    );
}
